import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Header from '../components/Header';
import Menu from '../components/Menu';
import FontSizeSelector from '../components/FontSizeSelector';
import { leaveFamilyWeb } from '../services/WebCloudFunctionsService';

const UserSettingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuth();
  const { theme, toggleTheme, setTheme, setFontSize, isDarkMode } = useTheme();
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleResetTheme = async () => {
    try {
      setTheme(false); // Reset to light theme
      console.log('Theme preference reset to light mode');
    } catch (error) {
      console.error('Error resetting theme:', error);
    }
  };

  const handleResetFontSize = async () => {
    try {
      setFontSize('medium'); // Reset to medium font size
      console.log('Font size preference reset to medium');
    } catch (error) {
      console.error('Error resetting font size:', error);
    }
  };

  const handleLeaveFamily = async () => {
    if (window.confirm('Are you sure you want to leave this family? You will no longer have access to organization challenges.')) {
      try {
        const result = await leaveFamilyWeb() as any;
        if (result.success) {
          alert('Successfully left the family');
          // The user context should automatically update when they refresh
          // or we could trigger a refresh here
        } else {
          alert(result.message || 'Failed to leave family');
        }
      } catch (error) {
        console.error('Error leaving family:', error);
        alert('Failed to leave family');
      }
    }
  };

  // Function to get user initials
  const getUserInitials = (displayName: string | null, email: string | null): string => {
    if (displayName) {
      const names = displayName.trim().split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return displayName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  // Function to determine if user has a profile image
  const hasProfileImage = (): boolean => {
    return !!(user?.profileImageURL);
  };

  // Function to copy user ID to clipboard
  const copyUserId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      alert(`User ID copied: ${user.id}`);
      console.log('User ID copied:', user.id);
    }
  };

  // Function to toggle account details visibility
  const toggleAccountDetails = () => {
    setShowAccountDetails(!showAccountDetails);
  };

  // Menu handlers
  const handleMenuPress = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  // Show loading state while authentication is being checked
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        color: theme.colors.text,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Only redirect to login if user is explicitly null after loading is complete
  if (!user && !isLoading) {
    navigate('/login');
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.text
    }}>
      {/* Header */}
      <Header
        title="Settings"
        showMenu={true}
        onMenu={handleMenuPress}
      />

      {/* Menu */}
      <Menu isOpen={menuOpen} onClose={handleMenuClose} />

      {/* Content */}
      <main style={{
        padding: '20px',
        maxWidth: '600px',
        margin: '0 auto',
        fontFamily: theme.fonts.primary,
      }}>
        {/* Profile Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '30px',
          padding: '20px 0',
        }}>
          {/* Profile Image Container */}
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50px',
            border: `3px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.surface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '15px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}>
            {hasProfileImage() ? (
              <img
                src={user!.profileImageURL}
                alt="Profile"
                style={{
                  width: '94px',
                  height: '94px',
                  borderRadius: '47px',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div style={{
                width: '94px',
                height: '94px',
                borderRadius: '47px',
                backgroundColor: theme.colors.primary,
                color: theme.colors.primaryText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: `${parseFloat(theme.typography.h1.fontSize) * 0.6}px`,
                fontWeight: 'bold',
                fontFamily: theme.fonts.display,
              }}>
                {getUserInitials(user!.displayName, user!.email)}
              </div>
            )}
          </div>

          {/* Profile Name */}
          <h2 style={{
            margin: '0 0 5px 0',
            fontSize: theme.typography.h3.fontSize,
            fontWeight: theme.typography.h3.fontWeight,
            fontFamily: theme.fonts.display,
            lineHeight: theme.typography.h3.lineHeight,
            letterSpacing: theme.typography.h3.letterSpacing,
            color: theme.colors.text,
            textAlign: 'center',
          }}>
            {user!.displayName || 'User'}
          </h2>

          {/* Profile Email */}
          <p style={{
            margin: '0',
            fontSize: theme.typography.caption.fontSize,
            fontWeight: theme.typography.caption.fontWeight,
            fontFamily: theme.fonts.secondary,
            lineHeight: theme.typography.caption.lineHeight,
            letterSpacing: theme.typography.caption.letterSpacing,
            color: theme.colors.textSecondary,
            textAlign: 'center',
          }}>
            {user!.email || 'No email provided'}
          </p>
        </div>

        {/* Account Details Section */}
        <div style={{
          backgroundColor: theme.colors.card,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: `1px solid ${theme.colors.border}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: theme.typography.h3.fontSize,
            fontWeight: theme.typography.h3.fontWeight,
            fontFamily: theme.fonts.display,
            lineHeight: theme.typography.h3.lineHeight,
            letterSpacing: theme.typography.h3.letterSpacing,
            color: theme.colors.text,
            textAlign: 'center',
          }}>
            Account Details
          </h3>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.colors.divider}`,
          }}>
            <span style={{
              color: theme.colors.textSecondary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
            }}>
              User ID:
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {showAccountDetails ? (
                <span style={{
                  color: theme.colors.text,
                  fontSize: theme.typography.body.fontSize,
                  fontWeight: theme.typography.body.fontWeight,
                  fontFamily: theme.fonts.secondary,
                  lineHeight: theme.typography.body.lineHeight,
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {user!.id || 'Not available'}
                </span>
              ) : (
                <span style={{
                  color: theme.colors.textSecondary,
                  fontSize: theme.typography.body.fontSize,
                  fontWeight: theme.typography.body.fontWeight,
                  fontFamily: theme.fonts.secondary,
                  lineHeight: theme.typography.body.lineHeight,
                }}>
                  ••••••••••••••••
                </span>
              )}
              <button
                onClick={toggleAccountDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.primary,
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 122, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                👁
              </button>
              {showAccountDetails && (
                <button
                  onClick={copyUserId}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.colors.success,
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(52, 199, 89, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  📋
                </button>
              )}
            </div>
          </div>

          {/* Organization */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.colors.divider}`,
          }}>
            <span style={{
              color: theme.colors.textSecondary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
            }}>
              Organization:
            </span>
            <span style={{
              color: theme.colors.text,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user?.organizationName || 'University Hospitals'}
            </span>
          </div>

          {/* Team */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.colors.divider}`,
          }}>
            <span style={{
              color: theme.colors.textSecondary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
            }}>
              Team:
            </span>
            <span style={{
              color: theme.colors.text,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user?.teamName || 'General'}
            </span>
          </div>

          {/* User Type */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.colors.divider}`,
          }}>
            <span style={{
              color: theme.colors.textSecondary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
            }}>
              User Type:
            </span>
            <span style={{
              color: theme.colors.text,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user?.role || 'Member'}
            </span>
          </div>

          {/* Auth Provider */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '12px',
            borderBottom: 'none',
          }}>
            <span style={{
              color: theme.colors.textSecondary,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
            }}>
              Auth Provider:
            </span>
            <span style={{
              color: theme.colors.text,
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user?.authProvider === 'azure' ? 'Azure AD' :
                user?.authProvider === 'google' ? 'Google' :
                  user?.authProvider === 'email' ? 'Email' :
                    user?.authProvider || 'Email'}
            </span>
          </div>
        </div>

        {/* Family Member Status Section */}
        {user?.familyStatus === 'family_member' && (
          <div style={{
            backgroundColor: theme.colors.card,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: `1px solid ${theme.colors.border}`,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: theme.typography.h3.fontSize,
              fontWeight: theme.typography.h3.fontWeight,
              fontFamily: theme.fonts.display,
              lineHeight: theme.typography.h3.lineHeight,
              letterSpacing: theme.typography.h3.letterSpacing,
              color: theme.colors.text,
              textAlign: 'center',
            }}>
              Family Member Status
            </h3>

            {/* Status */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '12px',
              borderBottom: `1px solid ${theme.colors.divider}`,
            }}>
              <span style={{
                color: theme.colors.textSecondary,
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
              }}>
                Status:
              </span>
              <span style={{
                color: theme.colors.success,
                fontSize: theme.typography.body.fontSize,
                fontWeight: '600',
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
              }}>
                Family Member
              </span>
            </div>

            {/* Organization */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '12px',
              borderBottom: `1px solid ${theme.colors.divider}`,
            }}>
              <span style={{
                color: theme.colors.textSecondary,
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
              }}>
                Organization:
              </span>
              <span style={{
                color: theme.colors.text,
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {user?.organizationName || 'Unknown'}
              </span>
            </div>

            {/* Invited By */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '12px',
              borderBottom: 'none',
            }}>
              <span style={{
                color: theme.colors.textSecondary,
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
              }}>
                Invited By:
              </span>
              <span style={{
                color: theme.colors.text,
                fontSize: theme.typography.body.fontSize,
                fontWeight: theme.typography.body.fontWeight,
                fontFamily: theme.fonts.secondary,
                lineHeight: theme.typography.body.lineHeight,
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {user?.familyMemberOf || 'Unknown'}
              </span>
            </div>

            {/* Leave Family Button */}
            <button
              onClick={handleLeaveFamily}
              style={{
                width: '100%',
                padding: '12px 20px',
                marginTop: '16px',
                backgroundColor: theme.colors.error,
                color: theme.colors.primaryText,
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.warning || theme.colors.error;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.error;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Leave Family
            </button>
          </div>
        )}

        {/* Appearance Section */}
        <div style={{
          backgroundColor: theme.colors.card,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: `1px solid ${theme.colors.border}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: theme.typography.h3.fontSize,
            fontWeight: theme.typography.h3.fontWeight,
            fontFamily: theme.fonts.display,
            lineHeight: theme.typography.h3.lineHeight,
            letterSpacing: theme.typography.h3.letterSpacing,
            color: theme.colors.text,
          }}>
            Appearance
          </h3>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <span style={{
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              color: theme.colors.text
            }}>
              Dark Mode
            </span>
            <button
              onClick={toggleTheme}
              style={{
                width: '48px',
                height: '24px',
                backgroundColor: isDarkMode ? theme.colors.primary : theme.colors.border,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s ease',
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: isDarkMode ? '26px' : '2px',
                transition: 'left 0.2s'
              }} />
            </button>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <span style={{
              fontSize: theme.typography.body.fontSize,
              fontWeight: theme.typography.body.fontWeight,
              fontFamily: theme.fonts.secondary,
              lineHeight: theme.typography.body.lineHeight,
              color: theme.colors.text
            }}>
              Font Size
            </span>
            <FontSizeSelector />
          </div>

          <button
            onClick={handleResetTheme}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              color: theme.colors.text,
              cursor: 'pointer',
              fontSize: theme.typography.button.fontSize,
              fontWeight: theme.typography.button.fontWeight,
              fontFamily: theme.fonts.primary,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: theme.typography.button.letterSpacing,
              marginBottom: '8px',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.surface;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Reset Theme Preferences
          </button>

          <button
            onClick={handleResetFontSize}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              color: theme.colors.text,
              cursor: 'pointer',
              fontSize: theme.typography.button.fontSize,
              fontWeight: theme.typography.button.fontWeight,
              fontFamily: theme.fonts.primary,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: theme.typography.button.letterSpacing,
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.surface;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Reset Font Size
          </button>
        </div>

        {/* Account Section */}
        <div style={{
          backgroundColor: theme.colors.card,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: `1px solid ${theme.colors.border}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: theme.typography.h3.fontSize,
            fontWeight: theme.typography.h3.fontWeight,
            fontFamily: theme.fonts.display,
            lineHeight: theme.typography.h3.lineHeight,
            letterSpacing: theme.typography.h3.letterSpacing,
            color: theme.colors.text,
          }}>
            Account
          </h3>

          <button
            onClick={handleSignOut}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.colors.error,
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: theme.typography.button.fontSize,
              fontWeight: theme.typography.button.fontWeight,
              fontFamily: theme.fonts.primary,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: theme.typography.button.letterSpacing,
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d32f2f';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.error;
            }}
          >
            Sign Out
          </button>
        </div>
      </main>
    </div>
  );
};

export default UserSettingsScreen; 